import type {
  ProductSetupConfig,
  SetupTask,
  SetupTaskGroup,
} from "../../registry/types";
import {
  getEvidenceWindowDays,
  isAiAutomationEnabled,
  isDisputesBillingEnabled,
} from "./disputes-settings-accessors";
import { DISPUTES_INTAKE_QUESTIONS } from "./intake";

/**
 * Disputes/Reclaim setup tasks.
 *
 * Sequential — each task depends on the previous so the widget can chain
 * them. Billing is last by design: the merchant sees the dashboard and
 * can use Reclaim before billing is set up; billing flips them to the
 * 25%-on-win paid state.
 *
 *   AI automation → evidence window → billing
 *
 * `disputes/enabled` itself is no longer a task — intake submit flips
 * it (see setup-guide-host), so by the time the widget renders the
 * legacy "Set up Reclaim" predicate is already satisfied.
 */

const ENABLE_AI_AUTOMATION: SetupTask = {
  id: "enable-ai-automation",
  title: "Turn on AI evidence automation",
  description:
    "Let Reclaim AI generate and submit evidence on your behalf, automatically.",
  route: (teamId) => `/stores/${teamId}/finance/disputes/settings`,
  completionPredicate: isAiAutomationEnabled,
  requiredForLive: true,
  contextualModal: {
    title: "Turn on AI evidence automation",
    body: "Reclaim AI generates dispute evidence automatically — only billed when we win. You can switch it off any time.",
    ctaText: "Open evidence settings",
  },
};

const SET_EVIDENCE_WINDOW: SetupTask = {
  id: "set-evidence-window",
  title: "Choose your evidence window",
  description:
    "How many days back should Reclaim look when gathering evidence? Default is 5 days.",
  route: (teamId) => `/stores/${teamId}/finance/disputes/settings`,
  completionPredicate: (team) => getEvidenceWindowDays(team) != null,
  prerequisites: [ENABLE_AI_AUTOMATION.id],
  requiredForLive: false,
  contextualModal: {
    title: "Choose your evidence window",
    body: "Reclaim will gather order data, customer messages, and shipping records from this window when defending a chargeback. 5 days is the default; longer windows pull more context but take more time to assemble.",
    ctaText: "Open settings",
  },
};

const SET_UP_BILLING: SetupTask = {
  id: "set-up-billing",
  title: "Set up billing",
  description:
    "Accept billing through Shopify so Reclaim can charge a 25% success fee on disputes we win.",
  route: (teamId) => `/stores/${teamId}/finance/disputes/integrations`,
  completionPredicate: isDisputesBillingEnabled,
  prerequisites: [SET_EVIDENCE_WINDOW.id],
  requiredForLive: true,
  contextualModal: {
    title: "Set up billing",
    body: "Reclaim works on a 25%-on-win success fee — you only pay when we recover a chargeback. Billing accept happens through Shopify.",
    ctaText: "Open billing",
  },
};

const TASK_LIST = [ENABLE_AI_AUTOMATION, SET_EVIDENCE_WINDOW, SET_UP_BILLING];

// Single ordered group — widget renders it as a flat sequential list.
const GROUPS: SetupTaskGroup[] = [
  { id: "setup", title: "Setup", taskIds: TASK_LIST.map((t) => t.id) },
];

export const disputesProductConfig: ProductSetupConfig = {
  product: "disputes",
  displayName: "Reclaim setup",
  intake: { questions: DISPUTES_INTAKE_QUESTIONS },
  groups: GROUPS,
  tasks: Object.fromEntries(TASK_LIST.map((t) => [t.id, t])),
  // Predicates only read team.settings.disputes — memoize on that slice.
  relevantTeamSlice: (team) => team.settings?.disputes,
};
